import { cx } from 'cva'
import { type Variants } from 'motion/react'
import { type FC } from 'react'
import type { MegaMenuType } from '../../frontend-types'
import { EASE_OUT_CUBIC, MENU_DURATION } from '../../helpers/timing'
import { Corridor } from '../atoms/Corridor'
import { Grid } from '../atoms/Grid'
import { MegaMenuItem } from '../molecules/MegaMenuItem'
import { MegaMenuMobile } from '../molecules/MegaMenuMobile'
import type { MegaMenuSideProps } from '../molecules/MegaMenuSide'
import { MegaMenuSide } from '../molecules/MegaMenuSide'

export type MegaMenuProps = {
	megaMenu?: MegaMenuType
	megaMenuSide?: MegaMenuSideProps
}

export const megaMenuTitleVariants: Variants = {
	hidden: {
		opacity: 0,
		y: 10,
		transition: { duration: MENU_DURATION, ease: EASE_OUT_CUBIC },
	},
	visible: {
		opacity: 1,
		y: 0,
		transition: { duration: MENU_DURATION, ease: EASE_OUT_CUBIC, delay: 0.15 },
	},
}

export const megaMenuItemVariants = (hasItemBelow: boolean): Variants => ({
	hidden: {
		opacity: 0,
		y: 10,
		transition: { duration: MENU_DURATION, ease: EASE_OUT_CUBIC },
	},
	visible: {
		opacity: 1,
		y: 0,
		transition: {
			duration: MENU_DURATION,
			ease: EASE_OUT_CUBIC,
			delay: hasItemBelow ? 0.2 : 0.3,
		},
	},
})

export const MegaMenu: FC<MegaMenuProps> = ({ megaMenu, megaMenuSide }) => {
	const { title, menuItems } = megaMenu ?? {}
	return (
		<Corridor className="min-h-[calc(100lvh-var(--spacing-header-height))]">
			<Grid className="pb-6xl content-start">
				<div className="col-span-full pt-5xl tablet-l:col-span-8 tablet-l:pt-4xl">
					<div className="w-full border-b-(length:--stroke-xs) border-b-blue-tint-40">
						{title && <span className="mb-xl block text-h4 text-blue-tint-10">{title}</span>}
					</div>
					<nav className="hidden grid-cols-1 gap-x-xl gap-y-4xl tablet-l:grid tablet-l:grid-cols-2">
						{menuItems?.map((item, index, items) => {
							const hasItemBelow = items[index + 2] !== undefined
							return (
								<div
									key={item.title || index}
									className={cx(
										'col-span-full tablet-l:col-span-1',
										hasItemBelow && 'border-b-(length:--stroke-xs) border-b-blue-tint-40 pt-4xl',
									)}
								>
									<MegaMenuItem megamenuItem={item} />
								</div>
							)
						})}
					</nav>
				</div>
				{menuItems && menuItems.length > 0 && <MegaMenuMobile items={menuItems} />}
				<div className="col-span-full pt-2xl tablet-l:col-span-3 tablet-l:col-start-10 tablet-l:pt-4xl">
					{megaMenuSide && <MegaMenuSide {...megaMenuSide} />}
				</div>
			</Grid>
		</Corridor>
	)
}
